import { useCallback, useId, useState } from 'react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/solid';
import './CounterModern.css';

const clamp = (value, min, max) => {
    let next = value;
    if (typeof min === 'number') next = Math.max(min, next);
    if (typeof max === 'number') next = Math.min(max, next);
    return next;
};

const normalizeValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const digitsOnly = (raw) => String(raw ?? '').replace(/\D/g, '');

const NON_DIGIT_KEYS = new Set(['-', '+', 'e', 'E', '.', ',']);

export const CounterModern = ({
    id: idProp,
    value = 0,
    onChange,
    min = 0,
    max,
    step = 1,
    label,
    required = false,
    error = null,
    disabled = false,
    editable = false,
    className = '',
    'aria-label': ariaLabel,
}) => {
    const generatedId = useId();
    const fieldId = idProp || generatedId;
    const numericValue = normalizeValue(value);
    const atMin = typeof min === 'number' && numericValue <= min;
    const atMax = typeof max === 'number' && numericValue >= max;

    const [isEditing, setIsEditing] = useState(false);
    const [inputText, setInputText] = useState('');

    const emitUpdater = useCallback((updater) => {
        if (!onChange) return;
        const current = normalizeValue(value);
        const next = typeof updater === 'function' ? updater(current) : updater;
        onChange(clamp(normalizeValue(next), min, max));
    }, [max, min, onChange, value]);

    const commitInputValue = useCallback((raw) => {
        const cleaned = digitsOnly(raw);
        const parsed = cleaned === '' ? min : parseInt(cleaned, 10);
        const next = clamp(Number.isFinite(parsed) ? parsed : min, min, max);
        onChange?.(next);
        return String(next);
    }, [max, min, onChange]);

    const decrement = () => {
        emitUpdater((prev) => prev - step);
    };

    const increment = () => {
        emitUpdater((prev) => prev + step);
    };

    const handleInputFocus = () => {
        setIsEditing(true);
        setInputText(String(numericValue));
    };

    const handleInputBlur = () => {
        setIsEditing(false);
        const committed = commitInputValue(inputText);
        setInputText(committed);
    };

    const handleInputChange = (event) => {
        const nextDigits = digitsOnly(event.target.value);
        setInputText(nextDigits);
        if (nextDigits === '') return;
        const parsed = parseInt(nextDigits, 10);
        if (!Number.isFinite(parsed)) return;
        onChange?.(clamp(parsed, min, max));
    };

    const handleInputKeyDown = (event) => {
        if (NON_DIGIT_KEYS.has(event.key)) {
            event.preventDefault();
        }
    };

    const handleInputPaste = (event) => {
        event.preventDefault();
        const pasted = event.clipboardData.getData('text');
        const nextDigits = digitsOnly(pasted);
        setInputText(nextDigits);
        if (nextDigits === '') return;
        const parsed = parseInt(nextDigits, 10);
        if (!Number.isFinite(parsed)) return;
        onChange?.(clamp(parsed, min, max));
    };

    const controlLabel = ariaLabel || label || 'Contador numérico';

    return (
        <div className={`counter-modern ${className}`.trim()}>
            {label && (
                <label id={`${fieldId}-label`} htmlFor={editable ? fieldId : undefined} className="counter-modern__label">
                    {label}
                    {required && (
                        <span className="counter-modern__required" aria-hidden="true">
                            *
                        </span>
                    )}
                </label>
            )}

            <div
                className={[
                    'counter-modern__field',
                    disabled ? 'counter-modern__field--disabled' : '',
                ].filter(Boolean).join(' ')}
            >
                <div
                    className="counter-modern__control"
                    role="group"
                    aria-labelledby={label ? `${fieldId}-label` : undefined}
                    aria-label={!label ? controlLabel : undefined}
                >
                <button
                    type="button"
                    className="counter-modern__btn counter-modern__btn--decrement"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        decrement();
                    }}
                    disabled={disabled || atMin}
                    aria-label="Disminuir"
                >
                    <MinusIcon aria-hidden="true" />
                </button>

                {editable ? (
                    <input
                        id={fieldId}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="counter-modern__value counter-modern__input"
                        value={isEditing ? inputText : String(numericValue)}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        onPaste={handleInputPaste}
                        disabled={disabled}
                        aria-invalid={error ? true : undefined}
                        aria-valuemin={min}
                        aria-valuemax={max}
                        aria-valuenow={numericValue}
                    />
                ) : (
                    <span
                        id={fieldId}
                        className="counter-modern__value"
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        {numericValue}
                    </span>
                )}

                <button
                    type="button"
                    className="counter-modern__btn counter-modern__btn--increment"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        increment();
                    }}
                    disabled={disabled || atMax}
                    aria-label="Aumentar"
                >
                    <PlusIcon aria-hidden="true" />
                </button>
                </div>
            </div>

            {error && (
                <p className="counter-modern__error" role="alert">
                    <svg className="counter-modern__error-icon" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {error}
                </p>
            )}
        </div>
    );
};
